import { fetchApi } from "~vue/api";
import jwtToken from "~vue/api/token.js";

import { logError } from "./logError";

export { logError };

/**
 * Our user interaction logging function that currently sends data to amplitude.
 *
 * @param {string} eventType name of the event being logged
 * @param {Object} extraData any additional data to be passed along with the event logging
 * @param {string} coachingSessionUuid coaching session UUID; when provided, the event is tied to that session
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const logUserInteraction = async (eventType: string, extraData: any = {}, coachingSessionUuid: string | undefined = undefined) => {
    const data = {
        event_type: eventType,
        extra_data: extraData,
        path: globalThis.location.pathname,
        now: extraData?.now ?? new Date().toISOString(),
    };

    // When using a session-scoped feedback token, the coaching session is embedded in the JWT.
    // Use the /session endpoint which reads the 'cs' claim from the token.
    const isFeedbackToken = jwtToken.isFeedbackSession();
    let endpoint: string;
    if (isFeedbackToken && coachingSessionUuid) {
        endpoint = "/v1/events/ux/anon";
    } else if (coachingSessionUuid) {
        endpoint = `/v1/events/ux/${coachingSessionUuid}`;
    } else {
        endpoint = "/v1/events/ux";
    }

    return fetchApi(endpoint, {
        method: "POST",
        body: JSON.stringify(data),
    }).catch(() => {});
};

/**
 * Attribution slug from the page's ?source= query param, or the page's own default.
 *
 * Notification/campaign deep links tag their landing page with ?source=<slug> (e.g. the
 * Teams feature nudge links with ?source=teams_notification) so the page's Amplitude events
 * can attribute the visit to the surface that drove it. Shape-validated because the value is
 * caller-controlled input headed for event data — anything that isn't a short slug falls
 * back to the default. Mirrors nadia_events.helpers.validated_source_param on the backend.
 */
export const analyticsSourceFromQuery = (defaultSource: string): string => {
    const source = new URLSearchParams(globalThis.location.search).get("source") ?? "";
    return /^[a-z0-9_-]{1,64}$/.test(source) ? source : defaultSource;
};

/**
 * Creates a WebSocket error handler that logs detailed error information.
 *
 * @param {Object} context context object containing session identifiers
 * @param {string} componentName name of the component using the WebSocket (e.g., 'ChatDetail', 'RealtimeChat')
 * @param {Function} onErrorCallback optional callback to run after logging (e.g., to set user-facing error message)
 * @returns {Function} error handler function for WebSocket errors
 *
 * @example
 * const onError = createWebSocketErrorHandler(
 *     { cs_id, cs_uuid, userCoachExchangeId },
 *     'ChatDetail',
 *     () => {
 *         chatError.value = "Trouble connecting to the chat. Please refresh and try again.";
 *     }
 * );
 * socketClient.value.onError = onError;
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const createWebSocketErrorHandler = (context: any, componentName: string, onErrorCallback: (e: any) => void = () => {}) => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return (e: any) => {
        const errorDetails = {
            type: e?.type,
            url: e?.target?.url,
            readyState: e?.target?.readyState,
            readyStateText: ["CONNECTING", "OPEN", "CLOSING", "CLOSED"][e?.target?.readyState] || "UNKNOWN",
            protocol: e?.target?.protocol,
            timestamp: new Date().toISOString(),
        };

        console.error(`WebSocket Error in ${componentName}:`, errorDetails, e);

        logError(`Could not connect to websocket in ${componentName}.`, {
            context: {
                websocket: errorDetails,
                session: {
                    cs_id: context.cs_id,
                    cs_uuid: context.cs_uuid,
                    uce_id: context.userCoachExchangeId?.value,
                },
                component: {
                    name: componentName,
                },
            },
            tags: {
                error_type: "websocket_connection",
                ready_state: errorDetails.readyStateText,
                component: componentName,
            },
        });

        if (typeof onErrorCallback === "function") {
            onErrorCallback(e);
        }
    };
};
